import { Linking } from 'react-native';
import { DateRange } from '../redux/dateRangeSlice';
import { UserInstance } from './Types';
import { Area, Floor, Space } from '../components/Map/SetFloorsModal/FloorItem';
import { Building } from '../screens/LocationManagement/components/AddBuildingModal';

export const isSameYear = (date: Date, comparisonDate: Date): boolean => !!date
  && !!comparisonDate && date.getFullYear() === comparisonDate.getFullYear();

export const isSameDate = (date: Date, comparisonDate: Date): boolean => (
  isSameYear(date, comparisonDate)
  && date.getMonth() === comparisonDate.getMonth()
  && date.getDate() === comparisonDate.getDate()
);

export const getRangeHours = (range: DateRange): number => (1 / 3600000)
  * (new Date(range.end).getTime() - new Date(range.start).getTime());

export const getPreviousDateRange = (range: DateRange): DateRange => {
  const end = range.start;
  const start = new Date(2 * new Date(range.start).getTime() - new Date(range.end).getTime())
    .toISOString();
  return { start, end };
};

export const showValueWithHyphenFallback = (value: string): string => (value?.trim().length > 0
  ? value : '-');

export const userFullName = (user: UserInstance): string => (user?.firstName
  ? getUserFullName(user) : user?.email);

export const openInNewTab = (url: string): void => {
  let fixedUrl: string = url;
  if (!fixedUrl.includes('http')) fixedUrl = `http://${fixedUrl}`;
  Linking.openURL(fixedUrl);
};

export const isValidValue = (value: unknown, type: unknown): boolean => Object
  .values(type).includes(value);

export const DEFAULT_TEMPLATE_TITLE = 'Report an issue with {{Asset:Name}} at {{Location:Name}}';

export const getFormattedTemplateTitle = (title: string, assetName: string): string => title
  ?.replace('{{Asset:Name}}', assetName)
  .replace(' at {{Location:Name}}', '')
  || 'Report issue';

export const getFormattedTemplateSubtitle = (subtitle: string): string => subtitle
  ?.replace('{/n}', '\n')?.replace('{\\n}', '\n') || 'What issues do you want to report?';

export const getLocationById = (id: string, buildings: object)
  : null | Building | Floor | Area | Space => {
  let location = null;
  Object.keys(buildings).forEach((buildingId: string) => {
    if (location) return;
    const building: Building = buildings[buildingId];
    if (buildingId === id) location = building;
    else {
      Object.keys(building.floors).forEach((floorId: string) => {
        if (location) return;
        const floor: Floor = building.floors[floorId];
        if (floorId === id) location = floor;
        else {
          Object.keys(floor.areas).forEach((areaId: string) => {
            if (location) return;
            const area: Area = floor.areas[areaId];
            if (areaId === id) location = area;
            else {
              Object.keys(area?.spaces ?? []).forEach((spaceId: string) => {
                if (location) return;
                const space: Space = area.spaces[spaceId];
                if (spaceId === id) location = space;
              });
            }
          });
        }
      });
    }
  });
  return location;
};

export const getLocationHierarchyById = (id: string, buildings: object)
  : { buildingId?: string, floorId?: string, areaId?: string, spaceId?: string } => {
  let found: boolean = false;
  const hierarchy = {
    buildingId: null,
    floorId: null,
    areaId: null,
    spaceId: null,
  };
  Object.keys(buildings).forEach((buildingId: string) => {
    if (found) return;
    const building: Building = buildings[buildingId];
    if (buildingId === id) {
      found = true;
      hierarchy.buildingId = buildingId;
    } else {
      Object.keys(building.floors).forEach((floorId: string) => {
        if (found) return;
        const floor: Floor = building.floors[floorId];
        if (floorId === id) {
          found = true;
          hierarchy.buildingId = buildingId;
          hierarchy.floorId = floorId;
        } else {
          Object.keys(floor.areas).forEach((areaId: string) => {
            if (found) return;
            const area: Area = floor.areas[areaId];
            if (areaId === id) {
              found = true;
              hierarchy.buildingId = buildingId;
              hierarchy.floorId = floorId;
              hierarchy.areaId = areaId;
            } else {
              Object.keys(area?.spaces ?? []).forEach((spaceId: string) => {
                if (found) return;
                if (spaceId === id) {
                  found = true;
                  hierarchy.buildingId = buildingId;
                  hierarchy.floorId = floorId;
                  hierarchy.areaId = areaId;
                  hierarchy.spaceId = spaceId;
                }
              });
            }
          });
        }
      });
    }
  });
  return hierarchy;
};

export const getUserFullName = (user: UserInstance): string => `${user?.firstName ? user.firstName : user.email} ${user?.lastName ?? ''}`;

export const getDateComponents = (dateAsString: string): number[] => dateAsString
  .replace(' ', '').split('/').map((value: string) => parseInt(value, 10));

export const getFileNameFromPath = (url: string): string => url?.split('/').pop();
